import type { Difficulty } from './types';

/** Tunable constants. Balance work happens here and in `apps/sim`, not in the reducer. */
export interface Rules {
  /** Units each player starts with, indexed by player count. */
  startingUnits: Readonly<Record<1 | 2 | 3 | 4, number>>;
  /** Horde starting units as a fraction of one player's starting units. */
  hordeShare: number;
  /** Horde share when exactly one player faces the horde alone. */
  hordeShareSolo: number;
  /** Zombies the horde drops on each territory it claims during setup. */
  hordeStack: number;
  /** Last faction standing wins only once it holds this share of the map. */
  dominationShare: number;
  /** Minimum reinforcements per turn. */
  minReinforcements: number;
  /** One reinforcement per this many territories. */
  territoriesPerReinforcement: number;
  /** Base chance a territory spawns a zombie at Rising. */
  baseSpawn: number;
  /** Added spawn chance per zombie already present. */
  spawnPerZombie: number;
  /** Spawn chance cap. */
  spawnCap: number;
  /** Chance step added per Mutation card. */
  mutationStep: number;
  /** A zombie's bite lands on a d6 roll of at least this. */
  biteThreshold: number;
  /** Card multiplier grows by one every this many rounds. */
  escalationEvery: number;
  /** Card multiplier never exceeds this. */
  escalationCap: number;
  /** Draw count grows by one every this many rounds. */
  drawEscalationEvery: number;
  /** Horde territories assault a neighbour every this many rounds. */
  spreadEvery: number;
  /** Fraction of a horde stack that shambles into the neighbour on an assault. */
  assaultFraction: number;
  /** Max hold cards in hand. */
  handLimit: number;
  /** Zombies a horde-held territory gains each round end, at minimum. */
  hordeGrowth: number;
  /** Horde stacks also grow by this fraction of themselves each round. */
  hordeGrowthRate: number;
  /** A spawn adds 1 zombie plus this fraction of those already present. */
  spawnBurst: number;
}

const base: Rules = {
  startingUnits: { 1: 40, 2: 30, 3: 25, 4: 20 },
  hordeShare: 0.5,
  hordeShareSolo: 0.6,
  hordeStack: 3,
  dominationShare: 0.5,
  minReinforcements: 3,
  territoriesPerReinforcement: 3,
  baseSpawn: 0.08,
  spawnPerZombie: 0.06,
  spawnCap: 0.6,
  mutationStep: 0.1,
  biteThreshold: 4,
  escalationEvery: 5,
  escalationCap: 6,
  drawEscalationEvery: 10,
  spreadEvery: 3,
  assaultFraction: 0.3,
  handLimit: 3,
  hordeGrowth: 2,
  hordeGrowthRate: 0.25,
  spawnBurst: 0.25,
};

export const RULES: Readonly<Record<Difficulty, Rules>> = {
  calm: {
    ...base,
    baseSpawn: 0.04,
    spawnPerZombie: 0.04,
    spawnBurst: 0.15,
    hordeShare: 0.35,
    hordeShareSolo: 0.4,
    hordeStack: 2,
    hordeGrowth: 1,
    hordeGrowthRate: 0.15,
    biteThreshold: 5,
    spreadEvery: 4,
    assaultFraction: 0.15,
  },
  uneasy: {
    ...base,
    baseSpawn: 0.06,
    spawnPerZombie: 0.05,
    spawnBurst: 0.2,
    hordeShare: 0.45,
    hordeShareSolo: 0.5,
    hordeGrowth: 2,
    hordeGrowthRate: 0.2,
    assaultFraction: 0.25,
  },
  outbreak: { ...base },
  apocalypse: {
    ...base,
    baseSpawn: 0.12,
    spawnPerZombie: 0.08,
    spawnCap: 0.75,
    spawnBurst: 0.35,
    hordeShare: 0.7,
    hordeShareSolo: 0.75,
    hordeStack: 4,
    hordeGrowth: 2,
    hordeGrowthRate: 0.3,
    biteThreshold: 3,
    escalationEvery: 4,
    spreadEvery: 2,
    assaultFraction: 0.35,
  },
};

export function rulesFor(difficulty: Difficulty): Rules {
  return RULES[difficulty];
}

/** Card magnitude multiplier: 1, then +1 every `escalationEvery` rounds, capped. */
export function escalation(rules: Rules, round: number): number {
  return Math.min(rules.escalationCap, 1 + Math.floor((round - 1) / rules.escalationEvery));
}

/** Cards drawn per turn for a given round. */
export function drawsFor(rules: Rules, round: number): number {
  return 1 + Math.floor((round - 1) / rules.drawEscalationEvery);
}
